import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';

interface Integration {
  id: string;
  user_id: string;
  provider: 'hubspot' | 'salesforce' | 'mailchimp' | 'google_sheets' | 'zapier';
  provider_account_id: string;
  is_active: boolean;
  last_sync_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface IntegrationLog {
  id: string;
  action: string;
  status: 'success' | 'failed' | 'pending';
  error_message: string | null;
  created_at: string;
}

export function useIntegrations() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Fetch all integrations
  const { data: integrations = [], isLoading } = useQuery({
    queryKey: ['integrations', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('integrations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Integration[];
    },
    enabled: !!user,
  });

  // Fetch integration logs
  const fetchLogs = (integrationId: string) => {
    return useQuery({
      queryKey: ['integration-logs', integrationId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('integration_logs')
          .select('*')
          .eq('integration_id', integrationId)
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) throw error;
        return data as IntegrationLog[];
      },
      enabled: !!integrationId,
    });
  };

  // Connect Integration (Generic)
  const connectIntegration = useMutation({
    mutationFn: async (provider: 'hubspot' | 'salesforce') => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${provider}-oauth?action=authorize`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to initiate OAuth');
      }

      const { authUrl } = await response.json();
      
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      window.open(
        authUrl,
        `${provider} OAuth`,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for OAuth callback
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(async () => {
          // Check if integration was created
          const { data } = await supabase
            .from('integrations')
            .select('*')
            .eq('user_id', user?.id)
            .eq('provider', provider)
            .maybeSingle();

          if (data) {
            clearInterval(checkInterval);
            resolve(data);
          }
        }, 1000);

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('OAuth timeout'));
        }, 300000);
      });
    },
    onSuccess: (_, provider) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success(`${provider} connected successfully!`);
    },
    onError: (error: Error, provider) => {
      toast.error(`Failed to connect ${provider}: ` + error.message);
    },
  });

  // Connect HubSpot (kept for backward compatibility or can be replaced)
  const connectHubSpot = connectIntegration;

  // Disconnect integration
  const disconnectIntegration = useMutation({
    mutationFn: async (provider: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${provider}-oauth?action=disconnect`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to disconnect');
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('Integration disconnected');
    },
    onError: (error: Error) => {
      toast.error('Failed to disconnect: ' + error.message);
    },
  });

  // Toggle integration active status
  const toggleIntegration = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('integrations')
        .update({ is_active: isActive })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('Integration updated');
    },
    onError: () => {
      toast.error('Failed to update integration');
    },
  });

  return {
    integrations,
    isLoading,
    connectIntegration,
    connectHubSpot,
    disconnectIntegration,
    toggleIntegration,
    fetchLogs,
  };
}
